import Integrations from "../../../components/common/Integrations/Integration";
import { IpNames } from "../../../interfaces/interfaces";
import { directConnectIps } from "../constants/quickConnect.constants";

export const REDIRECT_STASH_KEYS = {
  isUpdated: "isUpdated",
  uri: "RedirectURI",
  hash: "RedirectHash",
  expiry: "RedirectExpiry",
} as const;

export interface RedirectStash {
  uri: string | null;
  hash: string | null;
  expiryMs: number;
  isUpdatedRaw: string | null;
}

const readKey = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const readRedirectStash = (): RedirectStash => ({
  uri: readKey(REDIRECT_STASH_KEYS.uri),
  hash: readKey(REDIRECT_STASH_KEYS.hash),
  expiryMs: Number(readKey(REDIRECT_STASH_KEYS.expiry)),
  isUpdatedRaw: readKey(REDIRECT_STASH_KEYS.isUpdated),
});

export const isRedirectStashLive = (stash: RedirectStash, nowMs = Date.now()): boolean =>
  nowMs < stash.expiryMs;

export const readShopifyIsUpdatedFlag = (stash: RedirectStash, nowMs = Date.now()): boolean => {
  if (!stash.uri || !isRedirectStashLive(stash, nowMs) || !stash.isUpdatedRaw) return false;
  try {
    return Boolean(JSON.parse(stash.isUpdatedRaw.toLowerCase()));
  } catch {
    return false;
  }
};

export const CALLBACK_PARAM = {
  host: "host",
  properties: "properties",
  customers: "customers",
  adAccount: "adAccount",
  managerAccountsAmazonAds: "managerAccountsAmazonAds",
  fetchForAmazon: "fetchForAmazon",
} as const;
export type CallbackParam = (typeof CALLBACK_PARAM)[keyof typeof CALLBACK_PARAM];

/** Exactly Home's delete() list. `success` is deliberately absent. */
export const HOME_CLEAR_PARAM_KEYS = [
  "customers",
  "ip",
  "step",
  "ipName",
  "properties",
  "adAccount",
  "managerAccountsAmazonAds",
  "extraAccounts",
] as const;

export type AccountsParam = "properties" | "customers" | "adAccount" | "managerAccountsAmazonAds";

const ACCOUNTS_PARAM_ORDER: AccountsParam[] = [
  "properties",
  "customers",
  "adAccount",
  "managerAccountsAmazonAds",
];

export type CallbackMatch =
  | { kind: "host" }
  | { kind: "accounts"; param: AccountsParam; accountsJson: string | null }
  | { kind: "fetchForAmazon" }
  | { kind: "none" };

export interface DetectCallbackInput {
  params: URLSearchParams;
  extraAccounts: unknown[];
}

export const detectCallback = ({ params, extraAccounts }: DetectCallbackInput): CallbackMatch => {
  if (params.get(CALLBACK_PARAM.host)) return { kind: "host" };

  const hasExtra = Boolean(extraAccounts && extraAccounts.length);
  const matched = ACCOUNTS_PARAM_ORDER.find((param) => params.get(param) || hasExtra);
  if (matched) {
    return {
      kind: "accounts",
      param: matched,
      accountsJson: hasExtra ? JSON.stringify(extraAccounts) : params.get(matched),
    };
  }

  if (params.get(CALLBACK_PARAM.fetchForAmazon)) return { kind: "fetchForAmazon" };
  return { kind: "none" };
};

export type RedirectPlan =
  | { action: "navigate"; to: string }
  | { action: "openModal" }
  | { action: "toggleModal" }
  | { action: "none" };

export interface PlanRedirectInput {
  stash: RedirectStash;
  ip: { _id?: string; companyName?: string } & Record<string, unknown>;
  params: URLSearchParams;
  extraUrl: string;
  nowMs?: number;
}

const SELF_PATH = "/workspacestore";

/**
 * Legacy: Home read RedirectExpiry into a variable named isUpdated, and
 * Boolean("<any non-empty string>") is true, so `&isUpdated=true` is appended on
 * every live-stash replay. Connections.tsx only tests the param's presence and
 * never its value, so this is kept as-is deliberately.
 */
const LEGACY_ALWAYS_UPDATED = true;

export const buildReplayQuery = ({ stash, ip, params, extraUrl }: Omit<PlanRedirectInput, "nowMs">): string => {
  const redirectMust = `?ip=${JSON.stringify(ip)}&connectionHub=true&redirected=true`;
  const isUpdatedUrl = `&isUpdated=${String(LEGACY_ALWAYS_UPDATED)}`;
  const pcExtraUrl =
    ip?.companyName === IpNames.shopify && params.get("success") === "true"
      ? "&showProductState=true"
      : "";
  return redirectMust + extraUrl + isUpdatedUrl + pcExtraUrl + stash.hash;
};

export const planRedirectReplay = (input: PlanRedirectInput): RedirectPlan => {
  const { stash, nowMs = Date.now() } = input;
  if (!stash.uri) return { action: "none" };

  try {
    if (!isRedirectStashLive(stash, nowMs)) return { action: "toggleModal" };
    if (stash.uri.toLowerCase() === SELF_PATH) return { action: "openModal" };
    if (stash.uri.toLowerCase().includes("workspacestore")) return { action: "openModal" };
    return { action: "navigate", to: stash.uri + buildReplayQuery(input) };
  } catch {
    return { action: "none" };
  }
};

export interface AmazonAppStoreBounceInput {
  amazonCallbackUri: string | null;
  amazonState: string | null;
  ipId: string;
  user: { id: string | number; companyId: string | number };
}

export const buildAmazonAppStoreRedirect = ({
  amazonCallbackUri,
  amazonState,
  ipId,
  user,
}: AmazonAppStoreBounceInput): string | null => {
  if (!amazonCallbackUri || !amazonState) return null;
  const version = import.meta.env.VITE_AMAZON_SELLER_VERSION === "beta" ? "&version=beta" : "";
  const state = JSON.stringify({ ipId, userId: user.id, companyId: user.companyId });
  const redirectUri = `${import.meta.env.VITE_IP_DEV_REST_LOCATION}/integrations/amazonSeller/auth/callback`;
  return `${amazonCallbackUri}?amazon_state=${amazonState}&state=${state}&redirect_uri=${redirectUri}${version}`;
};

export type QuickConnectRoute =
  | { route: "integrations" }
  | { route: "zoho" }
  | { route: "agencyDirectConnect" }
  | { route: "unavailable" };

export const resolveQuickConnectRoute = ({
  ip,
  isAgency,
}: {
  ip?: { companyName?: string } | null;
  isAgency: boolean;
}): QuickConnectRoute => {
  if (!ip) return { route: "unavailable" };
  if (ip.companyName === "Zoho") return { route: "zoho" };
  if (isAgency && directConnectIps.has(ip.companyName as IpNames)) return { route: "agencyDirectConnect" };
  return { route: "integrations" };
};

export interface QuickConnectRequest {
  ip: { _id?: string; companyName?: string } & Record<string, unknown>;
  user: { id: string | number; companyId: string | number; role?: string };
  step?: number;
  updated?: boolean;
  storeName?: string;
  setStep: (step: number) => void;
  setIp: (ip: unknown) => void;
}

/** The only place allowed to call the positional Integrations(). */
export const startQuickConnect = ({
  ip,
  user,
  step = 1,
  updated = false,
  storeName = "",
  setStep,
  setIp,
}: QuickConnectRequest): void => {
  Integrations(ip.companyName, ip._id, user, ip, step, setStep, setIp, updated, storeName);
};

export const findQuickConnectPartner = <T extends { _id?: string; companyName?: string }>(
  partners: T[],
  channel: { ipId: string; ipName: string }
): T | undefined =>
  partners.find(
    (candidate) => candidate._id === channel.ipId || candidate.companyName === channel.ipName
  );
